// how-to-start/utils/run-with-concurrency.ts
//
// THE BOUNDED FAN-OUT THE DECK IS BUILT WITH, extracted so the two things
// that build decks share one copy of it.
//
// It used to live inside ../hooks/use-funnel-generation, which was fine while
// the funnel was the only caller. Slides now stream into the editor as they
// arrive (see ../deck-build-runner), and that runner is mounted outside the
// funnel entirely -- it has to keep generating after the funnel has closed and
// the editor has taken over the screen. Two copies of retry-and-give-up logic
// is exactly the kind of duplication that drifts, so there is one.

/**
 * Ceiling on per-slide generation calls in flight. The production wizard runs
 * these unbounded (one fire-and-forget call per mapping as it streams in), so
 * the backend copes with a whole deck at once; this is only a guard against a
 * pathologically long outline. At 3 a 15-slide deck took five sequential
 * waves, and one slide that could never succeed held a third of the pool.
 */
export const SLIDE_GENERATION_CONCURRENCY = 20;

/** Retries per slide before giving up on it, matching the production wizard. */
export const SLIDE_GENERATION_RETRIES = 2;

/**
 * Spacing between retries, as the wizard does (1s, then 2s). Retrying
 * instantly turns a deterministic failure into a burst.
 */
export const SLIDE_RETRY_BASE_DELAY_MS = 1000;

/**
 * A slide the backend can never map — the request body is identical on every
 * attempt, so retrying is guaranteed to fail. Currently raised when the layout
 * model returns something that isn't a real slideDesignId.
 */
export const isUnmappableSlide = (error: unknown) =>
  error instanceof Error && /unable to map slide/i.test(error.message);

export const delay = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    // The listener has to come off again: one signal spans a whole deck, so a
    // retry per slide would otherwise leave a listener behind on every wait.
    let id: ReturnType<typeof setTimeout>;
    const done = () => {
      clearTimeout(id);
      signal?.removeEventListener("abort", done);
      resolve();
    };
    id = setTimeout(done, ms);
    signal?.addEventListener("abort", done, { once: true });
  });

/**
 * Runs `generateOne` for every index in [0, total) with bounded concurrency,
 * preserving result order regardless of completion order. Each index is
 * retried up to `retries` times; one that still fails is left empty
 * (`undefined`) so a single bad slide can't take down the whole batch — the
 * caller decides what to do with the gaps. Workers stop picking up new work
 * (without throwing) once `signal` is aborted; cancellation is never retried
 * or counted as a failure.
 */
export const runWithConcurrency = async <T>(
  total: number,
  concurrency: number,
  generateOne: (index: number) => Promise<T>,
  onProgress?: (done: number, total: number) => void,
  signal?: AbortSignal,
  onResult?: (index: number, result: T | null) => void,
  retries = SLIDE_GENERATION_RETRIES,
): Promise<(T | undefined)[]> => {
  const results = new Array<T | undefined>(total);
  let cursor = 0;
  let done = 0;

  const worker = async () => {
    for (;;) {
      if (signal?.aborted) return;
      const index = cursor;
      cursor += 1;
      if (index >= total) return;

      let result: T | undefined;
      for (let attempt = 0; attempt <= retries; attempt += 1) {
        if (signal?.aborted) return;
        try {
          result = await generateOne(index);
          break;
        } catch (e) {
          if (
            signal?.aborted ||
            (e instanceof Error && e.name === "AbortError")
          ) {
            return;
          }
          // Same body every time — further attempts can only fail identically.
          if (isUnmappableSlide(e)) {
            console.error(`Slide ${index} cannot be mapped; not retrying`, e);
            break;
          }
          if (attempt === retries) {
            console.error(
              `Slide ${index} failed after ${retries + 1} attempts`,
              e,
            );
            break;
          }
          await delay(SLIDE_RETRY_BASE_DELAY_MS * (attempt + 1), signal);
        }
      }

      results[index] = result;
      if (!signal?.aborted) onResult?.(index, result ?? null);
      done += 1;
      onProgress?.(done, total);
    }
  };

  await Promise.all(
    Array.from({ length: Math.min(concurrency, total) }, () => worker()),
  );

  return results;
};
